import { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Confetti, Plus, PencilSimple, Trash, Gift, Flag, TextT, Coins, Check, X, Ticket, Diamond, Heart } from "@phosphor-icons/react";

const TYPE_META = {
  credits:              { label: "Credits",                   Icon: Coins,   tint: "text-blue-600 dark:text-blue-400" },
  banner_impressions:   { label: "Banner Impressions",        Icon: Flag,    tint: "text-fuchsia-600 dark:text-fuchsia-400" },
  text_ad_impressions:  { label: "Text Ad Impressions",       Icon: TextT,   tint: "text-cyan-600 dark:text-cyan-400" },
  surf_drawn_tickets:   { label: "Surf Drawn Tickets",        Icon: Ticket,  tint: "text-amber-600 dark:text-amber-400" },
  diamond_emeralds:     { label: "Diamond Emeralds",          Icon: Diamond, tint: "text-emerald-600 dark:text-emerald-400" },
  heart_chrysolite:     { label: "Heart Shaped Chrysolite",   Icon: Heart,   tint: "text-rose-600 dark:text-rose-400" },
};

const EMPTY_FORM = {
  id: null,
  name: "",
  milestone: 100,
  prize_type: "credits",
  amount: 50,
  recurring: true,
  active: true,
  start_date: "",
  end_date: "",
};

export default function AdminPrizePages() {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: s }] = await Promise.all([
        api.get("/admin/prize-rules"),
        api.get("/admin/prize-rules/stats"),
      ]);
      setRules(data.rules || []);
      const map = {};
      (s.stats || []).forEach((row) => { map[row.rule_id] = row; });
      setStats(map);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (rule) => {
    setForm({
      id: rule.id,
      name: rule.name || "",
      milestone: rule.milestone,
      prize_type: rule.prize_type,
      amount: rule.amount,
      recurring: !!rule.recurring,
      active: !!rule.active,
      start_date: rule.start_date || "",
      end_date: rule.end_date || "",
    });
    setEditingId(rule.id);
    setShowForm(true);
  };

  const save = async () => {
    if (saving) return;
    if (!form.milestone || form.milestone < 1) return toast.error("Milestone must be at least 1 surf");
    if (!form.amount || form.amount <= 0) return toast.error("Amount must be greater than 0");
    setSaving(true);
    try {
      const payload = {
        name: form.name?.trim() || null,
        milestone: parseInt(form.milestone, 10),
        prize_type: form.prize_type,
        amount: Number(form.amount),
        recurring: !!form.recurring,
        active: !!form.active,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      if (editingId) {
        await api.put(`/admin/prize-rules/${editingId}`, payload);
        toast.success("Prize rule updated");
      } else {
        await api.post("/admin/prize-rules", payload);
        toast.success("Prize rule created");
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule) => {
    try {
      await api.post(`/admin/prize-rules/${rule.id}/toggle`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete prize rule "${rule.name || `${rule.milestone} surfs`}"?\n\nHistory of past claims will be preserved.`)) return;
    try {
      await api.delete(`/admin/prize-rules/${rule.id}`);
      toast.success("Prize rule deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sortedRules = useMemo(() => {
    return [...rules].sort((a, b) => a.milestone - b.milestone);
  }, [rules]);

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Rewards</div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white inline-flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            <Confetti size={30} weight="fill" className="text-yellow-500" />
            Prize Pages
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
            Configurable milestone rewards. When a member completes a surf that lands on a matching milestone, they'll see a Prize Page before their next website and can atomically claim the reward exactly once per cycle.
          </p>
        </div>
        <button
          data-testid="prize-create-btn"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-yellow-950 font-black px-5 py-2.5 text-sm shadow-sm transition-colors duration-200"
        >
          <Plus size={16} weight="bold" /> New prize rule
        </button>
      </div>

      {showForm && (
        <div data-testid="prize-form" className="rounded-2xl border border-yellow-300 dark:border-yellow-500/40 bg-yellow-50 dark:bg-yellow-500/5 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-base font-black text-slate-900 dark:text-white">{editingId ? "Edit prize rule" : "New prize rule"}</div>
            <button data-testid="prize-form-close" onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"><X size={20} /></button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Label (optional)</span>
              <input
                data-testid="prize-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Century Bonus"
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Required successful sites</span>
              <input
                data-testid="prize-form-milestone"
                type="number" min="1" step="1"
                value={form.milestone}
                onChange={(e) => setForm({ ...form, milestone: e.target.value })}
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Prize type</span>
              <select
                data-testid="prize-form-type"
                value={form.prize_type}
                onChange={(e) => setForm({ ...form, prize_type: e.target.value })}
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              >
                {Object.entries(TYPE_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Prize amount</span>
              <input
                data-testid="prize-form-amount"
                type="number" min="0" step={form.prize_type === "credits" ? "0.1" : "1"}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Optional start date</span>
              <input
                data-testid="prize-form-start"
                type="datetime-local"
                value={form.start_date ? form.start_date.slice(0, 16) : ""}
                onChange={(e) => setForm({ ...form, start_date: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Optional end date</span>
              <input
                data-testid="prize-form-end"
                type="datetime-local"
                value={form.end_date ? form.end_date.slice(0, 16) : ""}
                onChange={(e) => setForm({ ...form, end_date: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              />
            </label>
            <label data-testid="prize-form-recurring" className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.recurring}
                onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
                className="h-4 w-4 accent-yellow-500"
              />
              <div>
                <div className="text-sm font-bold text-slate-900 dark:text-white">Recurring</div>
                <div className="text-[11px] text-slate-500">If off, each user can claim once total</div>
              </div>
            </label>
            <label data-testid="prize-form-active" className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="h-4 w-4 accent-yellow-500"
              />
              <div>
                <div className="text-sm font-bold text-slate-900 dark:text-white">Active</div>
                <div className="text-[11px] text-slate-500">Inactive rules never trigger</div>
              </div>
            </label>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              data-testid="prize-form-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-yellow-950 font-black px-5 py-2.5 text-sm shadow-sm disabled:opacity-60 transition-colors duration-200"
            >
              <Check size={16} weight="bold" /> {editingId ? "Save changes" : "Create prize rule"}
            </button>
            <button
              data-testid="prize-form-cancel"
              onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }}
              className="rounded-full px-4 py-2 text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div>
            <div className="text-base font-black text-slate-900 dark:text-white">Prize rules</div>
            <div className="text-xs text-slate-500">Lowest milestone always fires first when several rules match on the same surf.</div>
          </div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{rules.length} rule{rules.length !== 1 ? "s" : ""}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="p-3 text-left">Milestone</th>
                <th className="p-3 text-left">Prize</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-center">Recurring</th>
                <th className="p-3 text-center">Active</th>
                <th className="p-3 text-left">Window</th>
                <th className="p-3 text-right">Claims</th>
                <th className="p-3 text-right">Paid out</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && <tr><td colSpan={9} className="p-6 text-center text-slate-500">Loading…</td></tr>}
              {!loading && sortedRules.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-10 text-center text-slate-500">
                    <Gift size={30} weight="duotone" className="mx-auto text-yellow-500 mb-2" />
                    No prize rules yet. Click <span className="font-bold">New prize rule</span> to configure your first milestone reward.
                  </td>
                </tr>
              )}
              {sortedRules.map((r) => {
                const meta = TYPE_META[r.prize_type] || { label: r.prize_type, Icon: Gift, tint: "text-slate-500" };
                const st = stats[r.id] || {};
                const Icon = meta.Icon;
                return (
                  <tr key={r.id} data-testid={`prize-row-${r.milestone}`} className={r.active ? "" : "opacity-50"}>
                    <td className="p-3 font-black text-slate-900 dark:text-white">{r.milestone.toLocaleString()} sites</td>
                    <td className="p-3">
                      <div className="font-bold text-slate-900 dark:text-white">{r.name || <span className="text-slate-400 italic">unlabeled</span>}</div>
                      <div className="text-xs font-black text-slate-600 dark:text-slate-300">{Number(r.amount).toLocaleString()} {meta.label.toLowerCase()}</div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest ${meta.tint}`}>
                        <Icon size={14} weight="fill" /> {meta.label}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {r.recurring ? (
                        <span className="text-xs font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">Recurring</span>
                      ) : (
                        <span className="text-xs font-black uppercase tracking-widest text-slate-500">Once</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <button
                        data-testid={`prize-toggle-${r.milestone}`}
                        onClick={() => toggleActive(r)}
                        className={`relative inline-flex h-6 w-11 rounded-full transition-colors duration-200 ${r.active ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"}`}
                      >
                        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${r.active ? "translate-x-5" : "translate-x-1"} mt-0.5`} />
                      </button>
                    </td>
                    <td className="p-3 text-xs text-slate-500">
                      {(r.start_date || r.end_date) ? (
                        <div className="whitespace-nowrap">
                          {r.start_date && <div>from {new Date(r.start_date).toLocaleDateString()}</div>}
                          {r.end_date && <div>to {new Date(r.end_date).toLocaleDateString()}</div>}
                        </div>
                      ) : <span className="text-slate-400">Always</span>}
                    </td>
                    <td className="p-3 text-right font-black text-slate-900 dark:text-white">{st.claims || 0}</td>
                    <td className="p-3 text-right font-black text-slate-900 dark:text-white">{Number(st.total_amount || 0).toLocaleString()}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button data-testid={`prize-edit-${r.milestone}`} onClick={() => openEdit(r)} className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs font-bold hover:underline mr-3"><PencilSimple size={12} weight="bold" /> edit</button>
                      <button data-testid={`prize-delete-${r.milestone}`} onClick={() => remove(r)} className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs font-bold hover:underline"><Trash size={12} weight="bold" /> delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
